import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Bell, Check, CheckCheck, Trash2, Swords, Trophy, UserPlus, Calendar, AlertTriangle, X, BellOff, Award, ClipboardList, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";
import { usePushNotifications } from "@/hooks/use-push-notifications";

interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  data: any;
  createdAt: string;
  readAt: string | null;
  relatedMatchId: string | null;
  relatedLeagueId: string | null;
  relatedUserId: string | null;
  notificationEventId?: string | null;
}

interface NotificationCenterProps {
  onNavigate?: (path: string) => void;
  // Called when a "registra el resultado" reminder is clicked. The
  // parent should switch to the matches tab and auto-open the score
  // dialog for `matchId` (same behavior as the push deep link).
  onOpenMatchResultDialog?: (matchId: string) => void;
  // Called when a tournament notification is clicked and we know which
  // league it belongs to. The parent should open the leagues tab with
  // that league pre-selected and its Eventos/Torneos section active.
  onOpenLeagueEvents?: (leagueId: string) => void;
  // Opens the exact league's pending join-request management view.
  onOpenLeagueJoinRequests?: (leagueId: string) => void;
}

export function NotificationCenter({
  onNavigate,
  onOpenMatchResultDialog,
  onOpenLeagueEvents,
  onOpenLeagueJoinRequests,
}: NotificationCenterProps) {
  const [open, setOpen] = useState(false);
  const [optimisticSubscribed, setOptimisticSubscribed] = useState<boolean | null>(null);
  const { isSupported, isSubscribed, isSubscribing, isUnsubscribing, subscribe, unsubscribe } = usePushNotifications();

  const { data: notifications = [], isLoading } = useQuery<Notification[]>({
    queryKey: ['/api/notifications'],
  });

  const { data: unreadData } = useQuery<{ count: number }>({
    queryKey: ['/api/notifications/unread-count'],
  });

  const markAsReadMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('PATCH', `/api/notifications/${id}/read`, {});
      if (!response.ok) throw new Error("Error al marcar como leída");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('PATCH', '/api/notifications/read-all', {});
      if (!response.ok) throw new Error("Error al marcar todas como leídas");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await apiRequest('DELETE', `/api/notifications/${id}`, {});
      if (!response.ok) throw new Error("Error al eliminar");
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/notifications'] });
      queryClient.invalidateQueries({ queryKey: ['/api/notifications/unread-count'] });
    },
  });

  const unreadCount = unreadData?.count ?? 0;

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'match_pending_approval':
      case 'match_approved':
      case 'match_rejected':
        return <Swords className="h-4 w-4" />;
      case 'match_scheduled':
        return <Calendar className="h-4 w-4" />;
      case 'match_result_reminder':
        return <ClipboardList className="h-4 w-4" />;
      case 'league_join_request':
      case 'league_join_approved':
      case 'league_join_rejected':
        return <Trophy className="h-4 w-4" />;
      case 'match_deleted_by_admin':
        return <AlertTriangle className="h-4 w-4" />;
      case 'friend_request':
      case 'friend_request_accepted':
        return <UserPlus className="h-4 w-4" />;
      case 'achievement_unlocked':
        return <Award className="h-4 w-4" />;
      case 'tournament_draw_confirmed':
      case 'tournament_knockout_next_match':
      case 'tournament_completed':
        return <LayoutGrid className="h-4 w-4" />;
      default:
        return <Bell className="h-4 w-4" />;
    }
  };

  const getNotificationColor = (type: string) => {
    switch (type) {
      case 'match_pending_approval':
        return 'text-orange-500';
      case 'match_approved':
      case 'league_join_approved':
      case 'friend_request_accepted':
        return 'text-green-500';
      case 'match_rejected':
      case 'league_join_rejected':
      case 'match_deleted_by_admin':
        return 'text-red-500';
      case 'match_scheduled':
        return 'text-blue-500';
      case 'match_result_reminder':
        return 'text-orange-500';
      case 'friend_request':
      case 'league_join_request':
        return 'text-primary';
      case 'achievement_unlocked':
        return 'text-amber-500';
      case 'tournament_draw_confirmed':
      case 'tournament_knockout_next_match':
      case 'tournament_completed':
        return 'text-purple-500';
      default:
        return 'text-muted-foreground';
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    // Keep transactional open tracking separate from the read state and
    // campaign CTR metric. This is authenticated and idempotent server-side.
    void apiRequest(
      'POST',
      `/api/push/notifications/${encodeURIComponent(notification.notificationEventId ?? notification.id)}/open`,
      {},
    ).catch(() => {});

    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.id);
    }
    
    if (onNavigate) {
      switch (notification.type) {
        case 'match_pending_approval':
        case 'match_approved':
        case 'match_rejected':
        case 'match_scheduled':
          onNavigate('matches');
          break;
        case 'match_result_reminder':
          onNavigate('matches');
          if (notification.relatedMatchId && onOpenMatchResultDialog) {
            onOpenMatchResultDialog(notification.relatedMatchId);
          }
          break;
        case 'league_join_request':
          if (notification.relatedLeagueId && onOpenLeagueJoinRequests) {
            onOpenLeagueJoinRequests(notification.relatedLeagueId);
          } else {
            onNavigate('leagues');
          }
          break;
        case 'league_join_approved':
        case 'league_join_rejected':
        case 'match_deleted_by_admin':
          onNavigate('leagues');
          break;
        case 'friend_request':
        case 'friend_request_accepted':
          onNavigate('social');
          break;
        case 'achievement_unlocked':
          onNavigate('profile');
          break;
        case 'tournament_draw_confirmed':
        case 'tournament_knockout_next_match':
        case 'tournament_completed':
          // Land directly on the league's Eventos/Torneos section when
          // we know which league the tournament belongs to.
          if (notification.relatedLeagueId && onOpenLeagueEvents) {
            onOpenLeagueEvents(notification.relatedLeagueId);
          } else {
            onNavigate('leagues');
          }
          break;
      }
    }
    setOpen(false);
  };

  const handlePushToggle = async () => {
    const newState = !isSubscribed;
    setOptimisticSubscribed(newState);
    
    if (isSubscribed) {
      unsubscribe();
    } else {
      await subscribe();
    }
    
    // Reset optimistic state after a delay to let the mutation update the real state
    setTimeout(() => {
      setOptimisticSubscribed(null);
    }, 500);
  };

  const displaySubscribed = optimisticSubscribed !== null ? optimisticSubscribed : isSubscribed;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className="relative"
          data-testid="button-notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 right-0 h-4 min-w-[16px] px-1 flex items-center justify-center text-[9px] font-bold"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0" 
        align="end"
        data-testid="notifications-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b gap-2">
          <h3 className="font-semibold text-sm">Notificaciones</h3>
          <div className="flex items-center gap-1">
            {isSupported && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 transition-all"
                onClick={handlePushToggle}
                disabled={isSubscribing || isUnsubscribing}
                title={displaySubscribed ? "Desactivar notificaciones push" : "Activar notificaciones push"}
                data-testid="button-toggle-push-center"
              >
                {displaySubscribed ? (
                  <Bell className="h-4 w-4 text-primary transition-colors" />
                ) : (
                  <BellOff className="h-4 w-4 text-muted-foreground transition-colors" />
                )}
              </Button>
            )}
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs h-8"
                onClick={() => markAllAsReadMutation.mutate()}
                disabled={markAllAsReadMutation.isPending}
                data-testid="button-mark-all-read"
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Marcar todas
              </Button>
            )}
          </div>
        </div>
        
        <ScrollArea className="h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <Bell className="h-8 w-8 mb-2 opacity-50" />
              <p className="text-sm">No tienes notificaciones</p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    "flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-muted/50",
                    !notification.isRead && "bg-primary/5"
                  )}
                  onClick={() => handleNotificationClick(notification)}
                  data-testid={`notification-item-${notification.id}`}
                >
                  <div className={cn(
                    "mt-0.5 p-1.5 rounded-full bg-muted",
                    getNotificationColor(notification.type)
                  )}>
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={cn(
                        "text-sm font-medium truncate",
                        !notification.isRead && "font-semibold"
                      )}>
                        {notification.title}
                      </p>
                      {!notification.isRead && (
                        <div className="h-2 w-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                      {notification.message}
                    </p>
                    <p className="text-[10px] text-muted-foreground mt-1">
                      {formatDistanceToNow(new Date(notification.createdAt), { 
                        addSuffix: true, 
                        locale: es 
                      })}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 opacity-0 group-hover:opacity-100 hover:opacity-100 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteMutation.mutate(notification.id);
                    }}
                    data-testid={`button-delete-notification-${notification.id}`}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
